import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, AlertTriangle, Loader2, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VerificationTest {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'warning';
  message?: string;
}

const PreLaunchVerification: React.FC = () => {
  const [tests, setTests] = useState<VerificationTest[]>([
    {
      id: 'auth-flow',
      name: 'Authentication Flow',
      description: 'Verify user authentication and session management',
      status: 'pending'
    },
    {
      id: 'database-connection',
      name: 'Database Connection',
      description: 'Test database connectivity and queries',
      status: 'pending'
    },
    {
      id: 'course-data',
      name: 'Course Data Integrity',
      description: 'Verify all courses and modules are loaded correctly',
      status: 'pending'
    },
    {
      id: 'edge-functions',
      name: 'Edge Functions',
      description: 'Test critical edge function endpoints',
      status: 'pending'
    },
    {
      id: 'certificate-generation',
      name: 'Certificate Generation',
      description: 'Verify certificate generation system',
      status: 'pending'
    },
    {
      id: 'rls-policies',
      name: 'RLS Policies',
      description: 'Check Row Level Security policies are active',
      status: 'pending'
    }
  ]);
  const [isRunning, setIsRunning] = useState(false);

  const updateTest = (id: string, status: VerificationTest['status'], message?: string) => {
    setTests(prev => prev.map(test => 
      test.id === id ? { ...test, status, message } : test
    ));
  };

  const runTest = async (test: VerificationTest) => {
    updateTest(test.id, 'running');

    try {
      switch (test.id) {
        case 'auth-flow':
          await testAuthFlow();
          break;
        case 'database-connection':
          await testDatabaseConnection();
          break;
        case 'course-data':
          await testCourseData();
          break;
        case 'edge-functions':
          await testEdgeFunctions();
          break;
        case 'certificate-generation':
          await testCertificateGeneration();
          break;
        case 'rls-policies':
          await testRLSPolicies();
          break;
      }
    } catch (error) {
      updateTest(test.id, 'failed', error instanceof Error ? error.message : 'Test failed');
    }
  };

  const testAuthFlow = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) throw new Error(`Auth error: ${error.message}`);
    
    if (!session) {
      updateTest('auth-flow', 'warning', 'No active session (expected if not logged in)');
      return;
    }

    // Test profile creation
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (profileError) throw new Error(`Profile error: ${profileError.message}`);
    if (!profile) throw new Error('Profile not found');

    updateTest('auth-flow', 'passed', 'Authentication system working correctly');
  };

  const testDatabaseConnection = async () => {
    const { data, error } = await supabase
      .from('courses')
      .select('count')
      .single();

    if (error) throw new Error(`Database error: ${error.message}`);

    updateTest('database-connection', 'passed', 'Database connection established');
  };

  const testCourseData = async () => {
    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('id, title, level');

    if (coursesError) throw new Error(`Courses error: ${coursesError.message}`);
    if (!courses || courses.length === 0) throw new Error('No courses found');

    // Check for invalid levels
    const invalidLevels = courses.filter(c => 
      !['beginner', 'intermediate', 'advanced'].includes(c.level)
    );

    if (invalidLevels.length > 0) {
      throw new Error(`${invalidLevels.length} courses have invalid level values`);
    }

    const { data: modules, error: modulesError } = await supabase
      .from('course_modules')
      .select('count')
      .single();

    if (modulesError) throw new Error(`Modules error: ${modulesError.message}`);

    updateTest(
      'course-data', 
      'passed', 
      `${courses.length} courses and ${modules?.count || 0} modules verified`
    );
  };

  const testEdgeFunctions = async () => {
    // Test a public edge function
    const { data, error } = await supabase.functions.invoke('migrate-courses', {
      body: { action: 'check' }
    });

    if (error) {
      updateTest('edge-functions', 'warning', `Edge function test: ${error.message}`);
      return;
    }

    updateTest('edge-functions', 'passed', 'Edge functions responding correctly');
  };

  const testCertificateGeneration = async () => {
    const { data: templates, error } = await supabase
      .from('certificate_templates')
      .select('id, name');

    if (error) throw new Error(`Certificate templates error: ${error.message}`);
    if (!templates || templates.length === 0) {
      updateTest('certificate-generation', 'warning', 'No certificate templates found');
      return;
    }

    // Also check if certificates table is accessible
    const { data: certs, error: certsError } = await supabase
      .from('certificates')
      .select('count')
      .single();

    updateTest(
      'certificate-generation', 
      'passed', 
      `${templates.length} certificate template(s) and ${certs?.count || 0} certificates issued`
    );
  };

  const testRLSPolicies = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      updateTest('rls-policies', 'warning', 'Cannot test RLS without authentication');
      return;
    }

    // Test RLS by trying to access user-specific data
    const { data, error } = await supabase
      .from('user_module_progress')
      .select('id')
      .limit(1);

    if (error && error.message.includes('RLS')) {
      updateTest('rls-policies', 'passed', 'RLS policies are active and enforcing');
    } else {
      updateTest('rls-policies', 'passed', 'RLS policies verified');
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    toast.info('Running pre-launch verification tests...');

    for (const test of tests) {
      await runTest(test);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between tests
    }

    setIsRunning(false);
    
    const failedTests = tests.filter(t => t.status === 'failed');
    const passedTests = tests.filter(t => t.status === 'passed');
    
    if (failedTests.length === 0) {
      toast.success(`All ${passedTests.length} tests passed! System ready for launch.`);
    } else {
      toast.error(`${failedTests.length} test(s) failed. Review issues before launch.`);
    }
  };

  const getStatusIcon = (status: VerificationTest['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'running':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <div className="h-5 w-5 rounded-full bg-muted" />;
    }
  };

  const getStatusBadge = (status: VerificationTest['status']) => {
    const variants: Record<string, any> = {
      passed: 'default',
      failed: 'destructive',
      warning: 'outline',
      running: 'secondary',
      pending: 'outline'
    };

    return (
      <Badge variant={variants[status]} className="capitalize">
        {status}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Pre-Launch Verification</CardTitle>
          <Button 
            onClick={runAllTests} 
            disabled={isRunning}
            size="sm"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run All Tests
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {tests.map(test => (
          <div
            key={test.id}
            className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="mt-0.5">{getStatusIcon(test.status)}</div>
            
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-medium">{test.name}</h4>
                {getStatusBadge(test.status)}
              </div>
              <p className="text-sm text-muted-foreground">{test.description}</p>
              {test.message && (
                <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded">
                  {test.message}
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => runTest(test)}
              disabled={isRunning}
            >
              Test
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PreLaunchVerification;
